import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import { INSTRUMENTS } from '../Constants/Instruments'

const newId = () => Math.random().toString(36).slice(2, 10)

/**
 * Edit event roster positions (primary + band members).
 * mode: 'create' | 'edit'
 */
export default function PositionsEditor({
  positions = [],
  setPositions,
  artists = [],
  artistFee,
  onAssignApplicant,
  onInvite,
  giggingArtistIds = [],
  disabled = false,
}) {
  const [invitePosId, setInvitePosId] = useState('')
  const [inviteArtistId, setInviteArtistId] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteNumber, setInviteNumber] = useState('')
  const [newInstrument, setNewInstrument] = useState('Drums')
  const [newRole, setNewRole] = useState('member')

  const y = positions.length || 1
  const share = artistFee ? (Math.round((Number(artistFee) / y) * 100) / 100) : 0
  const x = positions.filter((p) => p.status === 'Confirmed').length

  const updatePosition = (id, patch) => {
    setPositions(positions.map((p) => (p.id === id ? { ...p, ...patch } : p)))
  }

  const addPosition = () => {
    const hasPrimary = positions.some((p) => p.role === 'primary')
    setPositions([
      ...positions,
      {
        id: newId(),
        role: hasPrimary ? newRole : 'primary',
        instrument: newInstrument,
        status: 'Open',
        applied: [],
        invited: false,
        artistId: '',
        artistName: '',
        artistEmail: '',
        artistNumber: '',
        artistImg: '',
        fee: share,
      },
    ])
  }

  const removePosition = (id) => {
    const next = positions.filter((p) => p.id !== id)
    if (next.length && !next.some((p) => p.role === 'primary')) {
      next[0] = { ...next[0], role: 'primary' }
    }
    setPositions(next)
  }

  const handleInviteSubmit = (positionId) => {
    if (!onInvite) return
    onInvite({
      positionId,
      artistId: inviteArtistId || undefined,
      name: inviteName,
      email: inviteEmail,
      number: inviteNumber,
    })
    setInvitePosId('')
    setInviteArtistId('')
    setInviteName('')
    setInviteEmail('')
    setInviteNumber('')
  }

  return (
    <Box sx={{ my: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        Roster {positions.length > 0 ? `(${x}/${y})` : ''}
        {artistFee && positions.length > 0 ? ` · £${share} each` : ''}
      </Typography>

      {positions.map((pos) => {
        const applicants = (pos.applied || [])
          .map((aid) => artists.find((a) => a._id === aid))
          .filter(Boolean)

        return (
          <Box
            key={pos.id}
            sx={{
              mb: 1.5,
              p: 1,
              backgroundColor: '#fafafa',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  value={pos.role}
                  disabled={disabled}
                  onChange={(e) => updatePosition(pos.id, { role: e.target.value })}
                >
                  <MenuItem value="primary">Primary</MenuItem>
                  <MenuItem value="member">Member</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Instrument</InputLabel>
                <Select
                  label="Instrument"
                  value={pos.instrument || ''}
                  disabled={disabled}
                  onChange={(e) => updatePosition(pos.id, { instrument: e.target.value })}
                >
                  {INSTRUMENTS.map((inst) => (
                    <MenuItem key={inst} value={inst}>{inst}</MenuItem>
                  ))}
                  {pos.instrument && !INSTRUMENTS.includes(pos.instrument) && (
                    <MenuItem value={pos.instrument}>{pos.instrument}</MenuItem>
                  )}
                </Select>
              </FormControl>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 100 }}>
                {pos.artistName
                  ? `${pos.artistName} · ${pos.status}${pos.invited ? ' (invited)' : ''}`
                  : `Open${pos.invited ? ' (locked)' : ''}`}
              </Typography>
              {!disabled && (
                <IconButton size="small" color="error" onClick={() => removePosition(pos.id)} aria-label="Remove position">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {pos.status === 'Open' && applicants.length > 0 && onAssignApplicant && (
              <FormControl size="small" fullWidth>
                <InputLabel>Assign applicant</InputLabel>
                <Select
                  label="Assign applicant"
                  value=""
                  onChange={(e) => onAssignApplicant(pos.id, e.target.value)}
                >
                  {applicants.map((a) => (
                    <MenuItem
                      key={a._id}
                      value={a._id}
                      sx={{ color: giggingArtistIds.includes(a._id) ? 'red' : 'inherit' }}
                    >
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {pos.status === 'Open' && onInvite && (
              <Box>
                {invitePosId === pos.id ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Existing artist</InputLabel>
                      <Select
                        label="Existing artist"
                        value={inviteArtistId}
                        onChange={(e) => setInviteArtistId(e.target.value)}
                      >
                        <MenuItem value="">New invite…</MenuItem>
                        {artists.map((a) => (
                          <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!inviteArtistId && (
                      <>
                        <TextField size="small" label="Name" value={inviteName} onChange={(e) => setInviteName(e.target.value)} />
                        <TextField size="small" label="Email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
                        <TextField size="small" label="Phone (+44…)" value={inviteNumber} onChange={(e) => setInviteNumber(e.target.value)} />
                      </>
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="contained" onClick={() => handleInviteSubmit(pos.id)}>
                        Send invite
                      </Button>
                      <Button size="small" onClick={() => setInvitePosId('')}>Cancel</Button>
                    </Box>
                  </Box>
                ) : (
                  <Button size="small" onClick={() => setInvitePosId(pos.id)}>Invite to slot</Button>
                )}
              </Box>
            )}
          </Box>
        )
      })}

      {!disabled && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Role</InputLabel>
            <Select label="Role" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="member">Member</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Instrument</InputLabel>
            <Select label="Instrument" value={newInstrument} onChange={(e) => setNewInstrument(e.target.value)}>
              {INSTRUMENTS.map((inst) => (
                <MenuItem key={inst} value={inst}>{inst}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={addPosition}>Add position</Button>
        </Box>
      )}
    </Box>
  )
}
